"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from "react";
import { toast } from "react-toastify";

import type { PaymentConfig } from '../types/payment-types';

// TypeScript interfaces
export interface AppConfig {
  app_id: string;
  app_name: string;
  api_base_url: string; // Base URL from applications table - used for both form builder API and custom endpoints
  backend_api_base_url?: string; // Base URL of the backend server (derived from config fetch URL) - used for payment API calls etc.
  base_url?: string; // Legacy support for backwards compatibility
  default_user_role: "super_admin" | "super_user";
  enable_version_history: boolean;
  enable_conditional_logic: boolean;
  enable_sections: boolean;
  max_forms_per_app?: number;
  max_flows_per_app?: number;
  primary_color?: string;
  logo_url?: string;
  features: Record<string, any> & {
    payment?: PaymentConfig;
  };
}

export interface FormBuilderContextType {
  // App configuration
  applicationId: string;
  appConfig: AppConfig | null;
  isLoading: boolean;
  error: string | null;

  // Helper methods
  refetchConfig: () => Promise<void>;
  getApiUrl: (endpoint: string) => string;

  // User role (can be overridden per component)
  currentUserRole: "super_admin" | "super_user";
  setCurrentUserRole: (role: "super_admin" | "super_user") => void;
}

const FormBuilderContext = createContext<FormBuilderContextType | undefined>(
  undefined
);

interface FormBuilderProviderProps {
  children: ReactNode;
  applicationId: string; // Required: unique identifier for the consuming app
  configApiUrl?: string; // Optional: custom URL to fetch config
  config?: Partial<AppConfig>; // Optional: directly provide config (skips API fetch)
  onConfigLoaded?: (config: AppConfig) => void; // Optional callback
  onError?: (error: string) => void; // Optional error callback
}

export const FormBuilderProvider: React.FC<FormBuilderProviderProps> = ({
  children,
  applicationId,
  configApiUrl,
  config: providedConfig,
  onConfigLoaded,
  onError,
}) => {
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<"super_admin" | "super_user">("super_user");

  // Fetch app configuration on mount (memoized to prevent recreating on every render)
  // Note: We don't include providedConfig, onConfigLoaded, or onError in dependencies
  // because they may be inline functions/objects from the parent, which would cause
  // infinite rerenders. Instead, we access them directly from the current scope.
  const fetchConfig = useCallback(async (background = false) => {
    // If config is provided directly, use it instead of fetching
    if (providedConfig) {
      const defaultConfig: AppConfig = {
        app_id: applicationId,
        app_name: providedConfig.app_name || "Embedded Form",
        api_base_url: providedConfig.api_base_url || "",
        default_user_role: providedConfig.default_user_role || "super_user",
        enable_version_history: providedConfig.enable_version_history ?? false,
        enable_conditional_logic: providedConfig.enable_conditional_logic ?? true,
        enable_sections: providedConfig.enable_sections ?? true,
        features: providedConfig.features || {},
        ...providedConfig,
      };

      setAppConfig(defaultConfig);
      if (!background) setIsLoading(false);

      if (onConfigLoaded) {
        onConfigLoaded(defaultConfig);
      }

      return;
    }

    if (!background) setIsLoading(true);
    setError(null);

    try {
      // Use custom config URL or build default
      const defaultBackendBase = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8000';
      const url = configApiUrl || `${defaultBackendBase}/api/v1/applications/${applicationId}/config`;

      const response = await fetch(url);

      // Derive the backend origin from the actual URL that was fetched
      const backendBaseUrl = new URL(response.url || url).origin;

      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.statusText}`);
      }

      const result = await response.json();

      // DEBUG: Log the full API response
      console.log('🔍 FormBuilderContext - Full API Response:', JSON.stringify(result, null, 2));

      if (result.code === 200 && result.data) {
        // Backend now returns { config: {...}, api_base_url: "..." }
        // Merge them into a single AppConfig object
        // backend_api_base_url = the backend server origin (for payment API calls etc.)
        // Fallback to base_url from config if api_base_url is not provided
        const mergedConfig = {
          ...result.data.config,
          api_base_url: result.data.api_base_url || result.data.config.base_url,
          backend_api_base_url: backendBaseUrl,
        };

        // DEBUG: Log the merged config
        console.log('🔍 FormBuilderContext - Merged Config:', JSON.stringify(mergedConfig, null, 2));
        console.log('🔍 FormBuilderContext - Payment Config:', mergedConfig.features?.payment);

        setAppConfig(mergedConfig);
        setCurrentUserRole(mergedConfig.default_user_role || "super_user");

        // Call optional success callback
        if (onConfigLoaded) {
          onConfigLoaded(mergedConfig);
        }
      } else {
        throw new Error(result.message || "Invalid config response");
      }
    } catch (err: any) {
      const errorMessage = err.message || "Failed to load app configuration";
      setError(errorMessage);
      toast.error(errorMessage);

      // Call optional error callback
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      if (!background) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  // Fetch config on mount and when applicationId changes
  useEffect(() => {
    if (applicationId) {
      fetchConfig();
    }
  }, [applicationId, fetchConfig]);

  // Helper to build full API URLs (memoized to prevent context rerenders)
  const getApiUrl = useCallback((endpoint: string): string => {
    if (!appConfig) return "";
    const baseUrl = appConfig.api_base_url.replace(/\/$/, ""); // Remove trailing slash
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    return `${baseUrl}${cleanEndpoint}`;
  }, [appConfig]);

  // Refetch configuration (useful for settings updates) - just use fetchConfig directly
  const refetchConfig = useCallback(() => fetchConfig(true), [fetchConfig]);

  const value: FormBuilderContextType = useMemo(() => ({
    applicationId,
    appConfig,
    isLoading,
    error,
    refetchConfig,
    getApiUrl,
    currentUserRole,
    setCurrentUserRole,
  }), [applicationId, appConfig, isLoading, error, refetchConfig, getApiUrl, currentUserRole]);

  // Show loading state while fetching config
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading app configuration...</p>
        </div>
      </div>
    );
  }

  // Show error state if config failed to load
  if (error || !appConfig) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6 bg-red-50 rounded-lg">
          <h2 className="text-red-800 text-xl font-semibold mb-2">Configuration Error</h2>
          <p className="text-red-600 mb-4">{error || "Failed to load app configuration"}</p>
          <button
            onClick={refetchConfig}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <FormBuilderContext.Provider value={value}>
      {children}
    </FormBuilderContext.Provider>
  );
};

// Custom hook to use the context
export const useFormBuilderContext = () => {
  const context = useContext(FormBuilderContext);
  if (context === undefined) {
    throw new Error(
      "useFormBuilderContext must be used within a FormBuilderProvider"
    );
  }
  return context;
};